import { useCallback, useEffect, useRef, useState } from "react"

import BrandMarquee from "./BrandMarquee"
import LotteryRoulette, {
	LOTTERY_WHEEL_MAX_MULTIPLIER,
} from "./LotteryRoulette"
import styles from "./LotteryRoulette.module.scss"
import {
	LOTTERY_CAMPAIGN_NAME,
	LOTTERY_PRIZE_GIFT_NAME,
	LOTTERY_WINNER_COUNT,
	lotteryPrizeText,
} from "./lotteryPrize"
import { hasSpunLottery, markLotteryAsSpun } from "./lotteryStorage"

import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import {
	FragmentType,
	getFragmentData,
	graphql,
} from "@/public/gql/__generated__"
import { toStringWithCommas } from "@/utils/strings/number"

// LOTTERY バナーが表示に使うフィールドは、自分の fragment で宣言する (fragment collocation).
// 親 (RereservationIncentiveBanner) は自分の fragment 内で ...lotteryBanner を spread し、
// unmask 結果 (この fragment の ref を含む) をそのまま渡してくる.
// export は Storybook の makeFragmentData 専用。useQuery / getFragmentData の引数として
// 他ファイルから使うのは禁止.
export const lotteryBannerFragment = graphql(/* GraphQL */ `
	fragment lotteryBanner on RereservationIncentiveOutput {
		price
		lotteryMultiplier
	}
`)

type Props = {
	incentive: FragmentType<typeof lotteryBannerFragment>
	cancelId: string
	customerName: string
	// お客様に抽選の案内を見せ終えた (ルーレットを回し終えた) 時に 1 度だけ呼ぶ.
	// サーバ (lottery_tickets.shown_time) への記録はページ側が行う.
	onShown: () => void
}

// LOTTERY (抽選キャンペーン) バナー.
// 上段で賞品 (抽選で当たるポイント) と応募条件 (再予約して来店) を、下段で当選確率 UP ルーレットを見せる.
// ルーレットは景品の抽選そのものではなく当選確率の倍率を上げるものなので、面を分けている.
// 未回転なら「ルーレットを回す」導線、回転後は確定した倍率を出し続ける.
// メール登録での倍率アップはここでは案内しない (予約確定後の完了画面で案内する).
const LotteryBanner = ({
	incentive,
	cancelId,
	customerName,
	onShown,
}: Props) => {
	const summary = getFragmentData(lotteryBannerFragment, incentive)

	// 回す演出を出すかはこの端末 (localStorage) で回したかだけで決める.
	// サーバの「見せた」記録 (lottery_tickets.shown_time) は測定用で、ここでは見ない.
	// QA で同じお客様の画面を別の端末から開いたときにも、ルーレットを回して確認できるようにするため.
	const [isSpun, setIsSpun] = useState(() =>
		hasSpunLottery("rereservation", cancelId)
	)
	const [isRouletteOpen, setIsRouletteOpen] = useState(false)
	// ルーレットを回している間〜結果表示の間だけ、モーダルの外側 (背景) をさらに暗くする.
	// 盤面と紙吹雪・花火に視線を集めるための演出.
	const [isStageDark, setIsStageDark] = useState(false)

	// バナー内の「ルーレットが回せる」枠. 自動で開くタイミングの判定に使う.
	const boostRef = useRef<HTMLDivElement>(null)

	// 未回転の人だけ、バナー内のルーレットの枠が画面の上へ流れて見えなくなった時点で 1 度だけ自動で開く.
	// 枠が見えている間に被せると、賞品・応募条件や「ルーレットを回す」ボタンを読む前に邪魔になる.
	// 読み飛ばして先へ進んだ人にだけ、改めてルーレットを見せる.
	useEffect(() => {
		if (isSpun) {
			return
		}
		const onScroll = () => {
			const boost = boostRef.current
			if (boost === null) {
				return
			}
			if (boost.getBoundingClientRect().bottom > 0) {
				return
			}
			setIsRouletteOpen(true)
			window.removeEventListener("scroll", onScroll)
		}
		window.addEventListener("scroll", onScroll, { passive: true })
		return () => window.removeEventListener("scroll", onScroll)
	}, [isSpun])

	const handleSpinStart = useCallback(() => {
		setIsStageDark(true)
	}, [])

	// 閉じたら暗転を戻す (次に開いた時は回す前の明るさから始める).
	const handleRouletteOpenChange = useCallback((open: boolean) => {
		setIsRouletteOpen(open)
		if (!open) {
			setIsStageDark(false)
		}
	}, [])

	const handleSpun = useCallback(() => {
		markLotteryAsSpun("rereservation", cancelId)
		setIsSpun(true)
		onShown()
	}, [cancelId, onShown])

	// LOTTERY は倍率 (lotteryMultiplier) とセットでしか意味をなさない.
	// サーバが欠かさず返す前提だが、万一 null なら抽選の訴求自体を出さない.
	if (summary.lotteryMultiplier === null) {
		return null
	}
	const multiplier = summary.lotteryMultiplier

	let rouletteContentClassName = styles.rouletteContent
	if (isStageDark) {
		rouletteContentClassName = `${styles.rouletteContent} ${styles.rouletteContentDark}`
	}

	let boost: JSX.Element
	if (isSpun) {
		boost = (
			<div className={`${styles.bannerBoost} ${styles.bannerBoostApplied}`}>
				<div className={styles.bannerBoostLabel}>ルーレットの結果</div>
				<div className={styles.bannerApplied}>
					<span>当選確率</span>
					<span className={styles.bannerAppliedNumber}>{multiplier}</span>
					<span className={styles.bannerAppliedUnit}>倍</span>
					<span>が適用されています</span>
				</div>
			</div>
		)
	} else {
		boost = (
			<div className={styles.bannerBoost} ref={boostRef}>
				<div className={styles.bannerBoostHead}>
					<div className={styles.bannerBoostWheel} aria-hidden="true" />
					<div className={styles.bannerBoostText}>
						<span className={styles.bannerBoostBadge}>期間限定</span>
						<div className={styles.bannerBoostHeadline}>
							今なら<strong>当選確率UP</strong>
							<br />
							ルーレットが回せる！
						</div>
					</div>
				</div>
				<div className={styles.bannerBoostMax}>
					当選確率が最大
					<span className={styles.bannerBoostMaxNumber}>
						{LOTTERY_WHEEL_MAX_MULTIPLIER}
					</span>
					倍に
				</div>
				<button
					type="button"
					className={styles.bannerCta}
					onClick={() => setIsRouletteOpen(true)}
				>
					ルーレットを回す
				</button>
			</div>
		)
	}

	return (
		<>
			<div className={styles.banner}>
				<div className={styles.bannerHeader}>
					<div className={styles.bannerHeaderTitle}>{customerName}様</div>
					<span className={styles.bannerHeaderBadge}>期間限定</span>
				</div>

				<div className={styles.bannerMain}>
					<div className={styles.bannerCampaignName}>
						{LOTTERY_CAMPAIGN_NAME}
					</div>
					<div className={styles.bannerSubtitle}>
						抽選で
						<strong className={styles.bannerSubtitleCount}>
							{LOTTERY_WINNER_COUNT}
						</strong>
						名様に当たる
					</div>
					<div className={styles.bannerAmountRow}>
						<div className={styles.bannerAmount}>
							{toStringWithCommas(summary.price)}
						</div>
						<div className={styles.bannerAmountUnit}>
							<span className={styles.bannerAmountUnitYen}>円分</span>
							<span>のポイント</span>
						</div>
					</div>
					<div className={styles.bannerNote}>
						{LOTTERY_PRIZE_GIFT_NAME}で好きなポイントに交換できます
					</div>
				</div>

				<BrandMarquee />

				{/* 応募条件は賞品 (金額と交換先のロゴ) のすぐ下で大きく見せる.
				    予約しただけで応募できたと誤解されやすいので「ご来店」を一番強く出す. */}
				<div className={styles.bannerCondition}>
					<div className={styles.bannerConditionLabel}>抽選の応募条件</div>
					<div className={styles.bannerConditionLead}>
						この画面から再予約して
					</div>
					<div className={styles.bannerConditionMain}>
						<strong className={styles.bannerConditionVisit}>ご来店</strong>
						<span>で応募完了</span>
					</div>
					<div className={styles.bannerConditionNote}>
						※ ご予約だけでは抽選の対象になりません
					</div>
				</div>

				{boost}

				{/* キャンペーンの細かい注記. 抽選番号は cancelId をそのまま見せる (問い合わせ時の照合用). */}
				<ul className={styles.bannerFinePrint}>
					<li>
						抽選番号:{" "}
						<span className={styles.bannerFinePrintNumber}>{cancelId}</span>
					</li>
					<li>この画面からのご予約のみ、適用です。</li>
					<li>ご来店の確認後、抽選結果をお知らせいたします。</li>
					<li>詳細はキャンペーン規約（ページ下部）ご確認ください。</li>
				</ul>
			</div>

			<ModalDialog
				isOpen={isRouletteOpen}
				onOpenChange={handleRouletteOpenChange}
				contentClassName={rouletteContentClassName}
			>
				<LotteryRoulette
					multiplier={multiplier}
					prizeText={lotteryPrizeText(summary.price)}
					customerName={customerName}
					onSpinStart={handleSpinStart}
					onSpun={handleSpun}
					onClose={() => handleRouletteOpenChange(false)}
				/>
			</ModalDialog>
		</>
	)
}

export default LotteryBanner
